import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';

// Base backend URL when running inside Capacitor native apps
export const REMOTE_API_BASE = process.env.VUE_APP_API_BASE_URL || 'https://live.triadmusic.app';

export const isNativePlatform = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Race contra timeout: garante que fluxos de boot/login nunca travem se a
 * rede pendurar (ex.: app reaberto com servidor fora do ar). Rejeita com
 * erro code='TIMEOUT' após ms.
 */
export const withTimeout = (promise, ms = 12000) => {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const err = new Error(`Timed out after ${ms}ms`);
      err.code = 'TIMEOUT';
      reject(err);
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer) clearTimeout(timer);
  });
};

export const getPlatform = () => {
  return Capacitor.getPlatform(); // 'android' | 'ios' | 'web'
};

let _isAmazonDeviceCached = null;

export const markAsAmazonDevice = () => {
  _isAmazonDeviceCached = true;
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem('triad_is_amazon_device', 'true');
    } catch (_) {
      // Ignored: storage quota or disabled in private mode
    }
  }
};

/**
 * Detects whether the current device is an Amazon Fire tablet or Fire OS device.
 * Checks:
 * 1. In-memory flag
 * 2. Persistent storage (localStorage)
 * 3. Broad userAgent patterns (Amazon, Silk, Kindle, FireOS, Fire Tablet, FireTV, KF*, AFT*)
 * 4. navigator.vendor
 */
export const isAmazonDevice = () => {
  if (_isAmazonDeviceCached !== null) {
    return _isAmazonDeviceCached;
  }
  if (typeof localStorage !== 'undefined') {
    try {
      if (localStorage.getItem('triad_is_amazon_device') === 'true') {
        _isAmazonDeviceCached = true;
        return true;
      }
    } catch (_) {
      // Ignored: storage quota or disabled in private mode
    }
  }
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';
  const vendor = navigator.vendor || '';

  // Expands match to all Amazon Fire tablets, Silk browsers, Kindle devices, Fire OS, and model identifiers
  const detected = /(Amazon|Silk|Kindle|FireOS|Fire\s*Tablet|FireTV|KF[A-Z0-9]{2,6}|AFT[A-Z0-9]{1,4})/i.test(ua)
    || /Amazon/i.test(vendor);

  if (detected) {
    _isAmazonDeviceCached = true;
    markAsAmazonDevice();
  }
  return detected;
};

/**
 * Asynchronous hardware-level check querying TriadLink native plugin (Build.MANUFACTURER / Build.MODEL)
 */
export const checkIsAmazonDeviceAsync = async () => {
  if (isAmazonDevice()) return true;
  if (!isNativePlatform()) return false;

  try {
    const { TriadLink } = await import('@/native/triad-link');
    if (typeof TriadLink.getDeviceInfo === 'function') {
      const info = await TriadLink.getDeviceInfo();
      if (info && (info.isAmazon || String(info.manufacturer).toLowerCase() === 'amazon' || String(info.brand).toLowerCase() === 'amazon')) {
        markAsAmazonDevice();
        return true;
      }
    }
  } catch (e) {
    // Silently continue
  }
  return false;
};

/**
 * Returns full URL for API or relative paths when running in native app
 */
export const resolveAppUrl = (path = '') => {
  if (!isNativePlatform()) {
    return path;
  }
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//')) {
    return path;
  }
  const cleanBase = REMOTE_API_BASE.replace(/\/+$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${cleanBase}${cleanPath}`;
};

/**
 * Base pública para links compartilháveis (ex.: convite de catálogo).
 * No web usa a origem atual; no app nativo usa o backend (REMOTE_API_BASE),
 * pois window.location.origin lá é capacitor://localhost / https://localhost.
 */
export const getPublicWebBaseUrl = () => {
  if (!isNativePlatform() && typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, '');
  }
  return REMOTE_API_BASE.replace(/\/+$/, '');
};

/**
 * Opens authentication or external URL in standard browser / ASWebAuthenticationSession
 */
export const openAuthSession = async (path = '/login') => {
  let targetUrl = resolveAppUrl(path);
  if (isNativePlatform()) {
    try {
      const urlObj = new URL(targetUrl);
      urlObj.searchParams.set('mobile', 'true');
      urlObj.searchParams.set('platform', getPlatform());
      urlObj.searchParams.set('return_url', 'mytriad://auth/callback');
      targetUrl = urlObj.toString();
      
      await Browser.open({
        url: targetUrl,
        windowName: '_self',
        toolbarColor: '#0f172a',
        presentationStyle: 'fullscreen'
      });
    } catch (e) {
      console.warn('Browser.open error, fallback to window.location:', e);
      window.location.href = targetUrl;
    }
  } else {
    window.location.href = path;
  }
};

/**
 * Opens an external web URL:
 * - On Native Mobile (App Android or Apple iOS, including Amazon Fire tablets):
 *   Opens in the device's system browser using Capacitor Browser plugin (or native fallback).
 * - On Web or Web PWA:
 *   Opens in browser using a new tab / page ('_blank').
 */
export const openExternalUrl = async (url, options = {}) => {
  if (!url) return;
  const target = options.target || '_blank';

  if (isNativePlatform()) {
    try {
      await Browser.open({
        url,
        windowName: target,
        toolbarColor: '#0f172a',
        presentationStyle: 'fullscreen'
      });
      return;
    } catch (e) {
      console.warn('Browser.open error, fallback to window.open:', e);
      try {
        window.open(url, '_system');
        return;
      } catch (fallbackErr) {
        console.warn('Fallback window.open failed:', fallbackErr);
      }
    }
  }

  // Web or Web PWA: Open in browser using _blank page
  const win = window.open(url, target, 'noopener,noreferrer');
  if (!win && typeof window !== 'undefined') {
    window.location.href = url;
  }
};

/**
 * Monitors when an external window or native in-app browser is closed by the user.
 * Dispatches callback once closed.
 *
 * Supports:
 * 1. Web & Web PWA: Polls `popupWindow.closed` + window focus/visibilitychange events.
 * 2. Native Mobile (Android & Apple iOS, including Amazon Fire):
 *    Listens to `@capacitor/browser` 'browserFinished' + `@capacitor/app` 'resume'.
 *
 * @param {Window|null} popupWindow - Window reference from window.open() on web
 * @param {Function} onClosed - Async callback executed when closed
 * @returns {Function} cleanup - Cleanup function to unregister listeners / clear timers
 */
export const watchExternalWindowClose = (popupWindow, onClosed) => {
  let isTriggered = false;
  let pollInterval = null;
  let browserHandle = null;
  let appHandle = null;

  const trigger = async () => {
    if (isTriggered) return;
    isTriggered = true;
    cleanup();
    if (typeof onClosed === 'function') {
      try {
        await onClosed();
      } catch (err) {
        console.warn('[watchExternalWindowClose] callback error:', err);
      }
    }
  };

  const cleanup = () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', checkClosed);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    if (browserHandle && typeof browserHandle.remove === 'function') {
      browserHandle.remove();
      browserHandle = null;
    }
    if (appHandle && typeof appHandle.remove === 'function') {
      appHandle.remove();
      appHandle = null;
    }
  };

  const checkClosed = () => {
    try {
      if (popupWindow && popupWindow.closed) {
        trigger();
      }
    } catch (err) {
      console.warn('[watchExternalWindowClose] checkClosed error:', err);
    }
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      checkClosed();
    }
  };

  // 1. Web & Web PWA: Poll popupWindow.closed and check on focus/visibility
  if (popupWindow && typeof popupWindow === 'object') {
    pollInterval = setInterval(checkClosed, 600);
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', checkClosed);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
  }

  // 2. Native Mobile (Android, iOS, Amazon Fire):
  if (isNativePlatform()) {
    try {
      Browser.addListener('browserFinished', () => {
        console.log('[watchExternalWindowClose] Native browser finished');
        trigger();
      }).then((handle) => {
        browserHandle = handle;
      }).catch((err) => {
        console.warn('Could not register browserFinished listener:', err);
      });
    } catch (e) {
      console.warn('Browser.addListener error:', e);
    }

    try {
      App.addListener('resume', () => {
        console.log('[watchExternalWindowClose] Native app resumed');
        trigger();
      }).then((handle) => {
        appHandle = handle;
      }).catch((err) => {
        console.warn('Could not register app resume listener:', err);
      });
    } catch (e) {
      console.warn('App.addListener error:', e);
    }
  }

  return cleanup;
};

/**
 * Setup deep link and app url listeners for authentication return
 */
export const initAppListeners = async (onAuthReturn) => {
  const safeNotify = (value) => {
    // Normaliza objeto { url } -> string; nunca deixa exceção quebrar o listener
    let normalized = value;
    if (normalized && typeof normalized === 'object' && typeof normalized.url === 'string') {
      normalized = normalized.url;
    }
    if (typeof onAuthReturn === 'function') {
      try {
        onAuthReturn(normalized);
      } catch (e) {
        console.warn('onAuthReturn handler error:', e);
      }
    }
  };
  if (isNativePlatform()) {
    // 1. Check if launched via deep link
    try {
      const launchUrl = await App.getLaunchUrl();
      if (launchUrl && launchUrl.url) {
        console.log('App launched with URL:', launchUrl.url);
        safeNotify(launchUrl.url);
      }
    } catch (e) {
      console.warn('Error checking launch URL:', e);
    }

    // 2. Listen for deep link events while running
    // IMPORTANTE: notifica PRIMEIRO e fecha o Browser depois (sem await).
    // Se não houver Browser aberto, Browser.close() pode pendurar e o
    // handler nunca chegaria ao processamento da URL.
    App.addListener('appUrlOpen', (data) => {
      console.log('App opened with URL: ', data && data.url);
      safeNotify(data && data.url);
      try {
        const p = Browser.close();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (e) {
        // Browser might already be closed
      }
    });

    // 3. Listen for app foregrounding/resume to refresh state
    App.addListener('appStateChange', async (state) => {
      if (state.isActive) {
        console.log('App resumed, checking auth status...');
        safeNotify(null);
      }
    });
  }
};

/**
 * Sets native status bar style and color based on current route and theme:
 * - Login screen: soft light blue with dark icons.
 * - Dark theme: dark background (#0F172A) with white icons.
 * - Light theme: light background (#F8F7FA) with dark icons.
 */
export const updateStatusBarTheme = async (isLogin = false) => {
  if (!isNativePlatform()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.show();
    if (isLogin) {
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setBackgroundColor({ color: '#E0E7FF' });
    } else {
      const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
      if (isDark) {
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#0F172A' });
      } else {
        await StatusBar.setStyle({ style: Style.Light });
        await StatusBar.setBackgroundColor({ color: '#F8F7FA' });
      }
    }
  } catch (err) {
    console.warn('[updateStatusBarTheme] status bar styling ignored or not supported:', err);
  }
};

/**
 * Sets initial native status bar based on route and theme
 */
export const initStatusBar = async () => {
  const isLoginRoute = typeof window !== 'undefined' && (window.location.hash.includes('/login') || window.location.pathname.includes('/login'));
  await updateStatusBarTheme(isLoginRoute);
};

